// @ts-strict-ignore
import { Linking } from 'react-native'
import Config from 'APP/Config'
import I18n from 'APP/I18n'
import { call, put, select } from 'typed-redux-saga'

// API Services
import Scribe from 'APP/Services/Scribe'
import * as NestedNavHelper from 'APP/Nav/NestedNavHelper'

// Helpers
import { handleNonRetryableRequestFailure } from 'APP/Lib/SagaRequestFailureHandler'
import type { RootState } from 'APP/Store/CreateStore'
import type { SagaActionProps } from 'APP/Models/Actions'

import type {
  AcceptFamilyInviteActionProps,
  AddAdultActionProps,
  CatchError,
  CreateChildProfileActionProps,
  RemoveAdultActionProps,
  UpdateChildProfileActionProps,
} from './types'
import { familyActions } from './slice'
import { selectFamilyState } from './selectors'
import { showToast } from 'APP/Converse/Toast/helpers'
import CoreData from 'APP/Services/Coredata'
import Assets from 'APP/Services/Assets'
import { selectPreferredLanguage } from 'APP/Redux/PatientRedux'

//TODO: Should be taken from related slice
export const patientState = (state: RootState) => state?.patient
export const loginState = (state: RootState) => state.login

export function* fetchFamily() {
  const patient = yield* select(patientState)
  const login = yield* select(loginState)
  const scribe = Scribe.create(login)

  try {
    const { familyId, family, permissions } = yield* call(
      scribe.getFamilyMembers,
      patient.profile.id
    )

    yield put(familyActions.fetchFamilySuccess({ familyId, family, permissions }))
  } catch (e) {
    const error = e as CatchError
    yield put(familyActions.familyError({ error }))
  }
}

export function* addAdult({ payload }: SagaActionProps<AddAdultActionProps>) {
  const { email } = payload
  const patient = yield* select(patientState)
  const login = yield* select(loginState)
  const scribe = Scribe.create(login)
  const brandVersion = Config.BRAND_VERSION
  const supportEmail = Config.SUPPORT_EMAIL[I18n.baseLocale]
  const ctaButtons = [
    {
      text: I18n.t('error.alert.dismiss'),
    },
    {
      text: I18n.t('error.alert.emailSupport'),
      onPress: () => {
        Linking.openURL(`mailto:${supportEmail}`)
      },
    },
  ]

  const userId = patient.profile.id as string

  try {
    const { member, familyId } = yield* call(scribe.addAdult, userId, email, brandVersion)
    yield put(familyActions.adultInviteSuccess({ member, familyId }))
    showToast({
      title: I18n.t('FamilyScreen.AddDependentScreen.alert.title'),
      description: I18n.t('FamilyScreen.AddDependentScreen.alert.description') || undefined,
      variant: 'success',
    })
  } catch (e) {
    const error = e as CatchError
    yield put(familyActions.familyError({ error }))
    if (error.code) {
      let errorBody
      let isGenericError = false
      switch (error.code) {
        case 'no_active_contract':
          errorBody = I18n.t('error.alert.invite.noActiveContract', { email })
          break
        case 'feature_not_in_contract':
          errorBody = I18n.t('error.alert.invite.noFamilyFeature', { email })
          break
        case 'already_in_family':
          errorBody = I18n.t('error.alert.invite.alreadInFamily', { email })
          break
        case 'already_on_contract':
          errorBody = I18n.t('error.alert.invite.alreadOnContract', { email })
          break
        case 'not_family_guardian':
          errorBody = I18n.t('error.alert.invite.notGuardian', { email })
          break
        case 'not_found':
        default:
          errorBody = I18n.t('error.alert.invite.generic', {
            email,
          })
          isGenericError = true
          break
      }
      yield call(
        handleNonRetryableRequestFailure,
        errorBody,
        undefined,
        isGenericError ? ctaButtons : undefined
      )
    } else {
      yield call(
        handleNonRetryableRequestFailure,
        I18n.t('error.alert.invite.generic', { email }),
        undefined,
        ctaButtons
      )
    }
  }
}

export function* removeAdult({ payload }: SagaActionProps<RemoveAdultActionProps>) {
  const { memberId, email } = payload
  const login = yield* select(loginState)
  const family = yield* select(selectFamilyState)
  const scribe = Scribe.create(login)

  try {
    yield* call(scribe.removeAdult, family.id, memberId)
    yield put(familyActions.cancelAdultInviteSuccess({ memberId: +memberId }))
  } catch (e) {
    const error = e as CatchError

    yield put(familyActions.familyError({ error }))
    if (error.code) {
      let errorBody
      switch (error.code) {
        case 'registered_user':
          errorBody = I18n.t('error.alert.removeAdult.registeredUser', { email })
          break
        case 'forbidden':
          errorBody = I18n.t('error.alert.removeAdult.notGuardian', { email })
          break
        case 'not_found':
        default:
          errorBody = I18n.t('error.alert.removeAdult.generic')
          break
      }
      yield call(handleNonRetryableRequestFailure, errorBody)
    } else {
      yield call(handleNonRetryableRequestFailure, I18n.t('error.alert.removeAdult.generic'))
    }
  }
}

export function* createChildProfile(action: SagaActionProps<CreateChildProfileActionProps>) {
  const patient = yield* select(patientState)
  const login = yield* select(loginState)
  const scribe = Scribe.create(login)

  try {
    const response = yield* call(scribe.addChild, patient.profile.id, action.payload.childData)
    yield* put(
      familyActions.updateChildProfileState({
        childProfile: response.child,
        familyId: response.familyId,
      })
    )
    showToast({
      title: I18n.t('FamilyScreen.AddDependentScreen.alert.addChildTitle'),
      variant: 'success',
    })
  } catch (e) {
    const error = e as CatchError
    yield* put(familyActions.familyError({ error }))
    if (error.code) {
      let errorBody
      switch (error.code) {
        case 'no_active_contract':
          errorBody = I18n.t('error.alert.createChild.noActiveContract')
          break
        case 'feature_not_in_contract':
          errorBody = I18n.t('error.alert.createChild.noFamilyFeature')
          break
        case 'not_family_guardian':
          errorBody = I18n.t('error.alert.createChild.notGuardian')
          break
        case 'not_found':
        default:
          errorBody = I18n.t('error.alert.createChild.generic')
          break
      }
      showToast({
        title: I18n.t('error.alert.title'),
        description: errorBody,
        variant: 'error',
      })
    } else {
      showToast({
        title: I18n.t('error.alert.title'),
        description: I18n.t('error.alert.createChild.generic') || undefined,
        variant: 'error',
      })
    }
  }
}

export function* updateChildProfile({ payload }: SagaActionProps<UpdateChildProfileActionProps>) {
  const login = yield* select(loginState)
  const family = yield* select(selectFamilyState)
  const scribe = Scribe.create(login)

  try {
    if (!family.id) throw { error: 'Family missed' }
    const response = yield* call(scribe.updateChild, family.id, payload.childId, payload.childData)
    if (payload.fileObj) {
      yield* call(scribe.uploadChildIdCard, response.url, response.fields, payload.fileObj)
    }
    yield* put(
      familyActions.updateChildProfileState({ childProfile: response.child, familyId: family.id })
    )
  } catch (e) {
    const error = e as CatchError
    yield* put(familyActions.familyError({ error }))
    if (error.code) {
      let errorBody
      switch (error.code) {
        case 'not_family_guardian':
          errorBody = I18n.t('error.alert.editChild.notGuardian')
          break
        case 'not_found':
        default:
          errorBody = I18n.t('error.alert.editChild.generic')
          break
      }
      yield* call(handleNonRetryableRequestFailure, errorBody)
    } else {
      yield* call(handleNonRetryableRequestFailure, I18n.t('error.alert.createChild.generic'))
    }
  }
  yield* call(NestedNavHelper.pop)
}

export function* acceptFamilyInvite({ payload }: SagaActionProps<AcceptFamilyInviteActionProps>) {
  const { familyId } = payload
  const patient = yield* select(patientState)
  const login = yield* select(loginState)
  const scribe = Scribe.create(login)

  try {
    yield* call(scribe.acceptFamilyInvite, patient.profile.id, familyId, {
      status: 'accepted',
    })

    yield put(familyActions.acceptFamilyInviteSuccess())

    // Refresh family data after accepting invite
    yield* call(fetchFamily)
  } catch (e) {
    const error = e as CatchError
    yield put(familyActions.familyError({ error }))
    yield call(handleNonRetryableRequestFailure, I18n.t('error.alert.acceptFamilyInvite'))
  }
}

export function* recordConsentAndCreateChildProfile({
  payload,
}: SagaActionProps<{
  childData: { givenName: string; familyName: string; dateOfBirth: string }
}>) {
  const login = yield* select(loginState)
  const languageCode = yield* select(selectPreferredLanguage)
  const coredataClient = CoreData.create(login.accessToken)
  const assetsClient = Assets.create(Config.ASSETS_DOMAIN)
  try {
    const type = 'guardian'
    const country = assetsClient.getConsentFormRegion() as string
    const brandId =
      Config.BRAND_ID && Config.BRAND_VERSION
        ? `${Config.BRAND_ID}_${Config.BRAND_VERSION}`
        : Config.BRAND_ID
    const version: string | null = yield call(
      assetsClient.getConsentFormVersion,
      brandId,
      country,
      languageCode,
      type
    )

    const consentPayload = {
      consent_type: type,
      consent_brand: brandId,
      version: version || '',
      language_iso_code: languageCode,
      country_iso_code: country,
    }

    yield call(coredataClient.updatePatient, {
      consents: [consentPayload],
    })
    yield* put(familyActions.createChildProfileRequest({ childData: payload.childData }))
  } catch (e) {
    const error = e as any
    yield* put(familyActions.familyError({ error }))
  }
}
